import React from 'react';

export const PHASE5_STRUCTURED_LABELS = [
  'Primary Exploration Direction', 'Top Patterns Addressed', 'Distress Theme', 'Primary Strength',
  'Active Strengths', 'Active Strengths Used', 'Dormant Strengths', 'Dormant Strengths Invited',
  'Buffer Consideration', 'Activation Plan',
  'Exploration Title', 'Confidence in Feasibility', 'Justification', 'Insight Addressed',
  'The Experiment', 'Experiment', 'Contingency Plan', 'Cultural and Contextual Fit', 'Strengths Leveraged',
  'Strengths Leverage Strategy', 'Behavioral Micro-Experiments', 'Core Theme', 'Pattern', 'Possible Meaning', 'Evidence',
  'Clinical Relevance', 'Clinical Relevance Note', 'Exploratory Question', 'Suggested Question', 'Strength', 'Risk',
  'Caution', 'Rationale', 'Suggested Dialogue', 'Therapeutic Use', 'Next Step', 'Priority',
  'Why This Matters', 'What To Explore', 'Potential Contradiction', 'Confidence', 'Member Check',
  'Narrative Anchor', 'Socratic Question', 'Follow-up Question', 'Key Finding', 'Important',
  'Main Pattern', 'Primary Concern', 'Protective Factor', 'Limitation', 'Low Confidence', 'Tentative',
  'Not a Diagnosis', 'Interpret With Caution', 'Needs Verification', 'Member Check Required',
  'Narrative Probe', 'Ask', 'Dialogue Prompt', 'Action', 'Try', 'Consider', 'Discuss', 'Explore',
  'Review', 'Follow-up', 'Topic', 'Theme', 'Why Explore This', 'Suggested Opening Question',
  'Evidence Anchor', 'Probe Area', 'Purpose', 'Primary Question', 'Evidence / Context',
  'Why Selected', 'Supporting Insight Evidence', 'Suggested Socratic Questions', 'Mandatory Disclaimer',
  'The Predicted Question', 'Predicted Question', 'Why It Matters', 'Suggested Opening', 'Suggested Opening Question',
  'Follow-Up', 'Member-Check Note', 'Member Check Note', 'Clinical / Contextual Caution',
  'Clinical and Contextual Caution', 'Primary Caution', 'Narrative Anchor',
  'The Predicted Objection', 'The Hidden Dynamic', 'The Righting Reflex Trap', 'The “Righting Reflex” Trap',
  'The MI Response Script',
  'Title', 'Author', 'Publication Year', 'Director', 'Year', 'Theme Match',
  'Why This Book', 'Why This Movie', 'Why This Music', 'Listening Focus', 'Reflection Questions',
  'Therapeutic Relevance', 'Possible Client Response', 'Hidden Dynamic', 'MI Response Script',
  'Righting Reflex Trap',
  'Key Themes', 'Protective Factors', 'Risks/Cautions', 'Action Steps', 'Follow-up Questions',
  'Possible Responses', 'Avoidances', 'Strengths Leveraged', 'Viewing Prompts',
];

const LABELS = PHASE5_STRUCTURED_LABELS;

const IMPORTANT = new Set(['key finding', 'important', 'priority', 'core theme', 'main pattern', 'primary concern', 'primary strength', 'strength', 'protective factor', 'next step', 'primary exploration direction', 'why this matters']);
const CAUTION = new Set(['primary caution', 'caution', 'limitation', 'low confidence', 'tentative', 'not a diagnosis', 'interpret with caution', 'needs verification', 'member check required', 'clinical relevance note', 'clinical / contextual caution', 'clinical and contextual caution', 'mandatory disclaimer', 'risk']);
const QUESTION = new Set(['the predicted question', 'predicted question', 'exploratory question', 'suggested question', 'socratic question', 'follow-up question', 'narrative probe', 'ask', 'dialogue prompt', 'suggested opening', 'suggested opening question', 'primary question', 'suggested socratic questions']);
const ACTION = new Set(['next step', 'action', 'try', 'consider', 'discuss', 'explore', 'review', 'follow-up']);
const EVIDENCE = new Set(['evidence', 'evidence anchor', 'evidence / context', 'supporting insight evidence']);
const LIST_VALUE_LABELS = new Set([
  'top patterns addressed', 'active strengths', 'dormant strengths', 'active strengths used',
  'dormant strengths invited', 'reflection questions', 'suggested socratic questions',
  'listening focus', 'key themes', 'protective factors', 'risks/cautions', 'action steps',
  'follow-up questions', 'possible responses', 'avoidances', 'strengths leveraged', 'viewing prompts',
]);
const labelPattern = new RegExp(`^(${LABELS.sort((a, b) => b.length - a.length).map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*:\\s*`, 'i');
const embeddedLabelPattern = new RegExp(`(?:^|\\s)(${LABELS.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*:\\s*`, 'gi');
const IMPORTANT_VALUES = new Set([
  'high', 'moderate', 'low', 'very low', 'important', 'priority', 'caution', 'warning',
  'risk', 'critical', 'strength', 'high confidence', 'low confidence', 'very low confidence',
  'not a diagnosis', 'mandatory disclaimer',
]);
const CHIP_LABELS = new Set(['confidence', 'confidence in feasibility', 'priority', 'risk', 'strength', 'caution', 'important']);

// Requiring whitespace after the marker prevents decimals and versions such as
// 4.6 and v2.1 from becoming list boundaries. Inline conversion also requires
// a real sequence of at least two markers.
export const splitExplicitNumberedSequence = (input) => {
  const text = String(input || '').trim();
  const matches = [...text.matchAll(/(?:^|\s)(\d+)([.):])\s+/g)];
  if (matches.length < 2 || matches[0].index !== 0) return null;
  if (!matches.every((match, index) => index === 0 || Number(match[1]) === Number(matches[index - 1][1]) + 1)) return null;
  return matches.map((match, index) => ({
    number: match[1],
    value: text.slice(match.index + match[0].length, matches[index + 1]?.index ?? text.length).trim(),
  })).filter((item) => item.value);
};

// A source paragraph may contain multiple labelled subsections. Segmenting it
// is display-only and prevents a later label from being swallowed by the last
// item in the preceding list.
export const splitEmbeddedStructuredSections = (input) => {
  const text = String(input || '').replace(/\s+/g, ' ').trim();
  const matches = [...text.matchAll(embeddedLabelPattern)];
  const patternMatches = [...text.matchAll(/(?:^|\s)(Pattern\s+\d+)\s*:\s*/gi)];
  const boundaries = [...matches.map((match) => ({ match, label: match[1], pattern: false })),
    ...patternMatches.map((match) => ({ match, label: match[1], pattern: true }))]
    .sort((a, b) => a.match.index - b.match.index)
    .filter((entry, index, all) => !index || entry.match.index !== all[index - 1].match.index);
  if (!boundaries.length || (boundaries.length === 1 && !boundaries[0].pattern)) return null;
  const segments = [];
  if (boundaries[0].match.index > 0) segments.push({ label: '', value: text.slice(0, boundaries[0].match.index).trim(), pattern: false });
  boundaries.forEach((entry, index) => {
    const start = entry.match.index + entry.match[0].length;
    const end = boundaries[index + 1]?.match.index ?? text.length;
    segments.push({ label: entry.label, value: text.slice(start, end).trim(), pattern: entry.pattern });
  });
  return segments.filter((segment) => segment.label || segment.value);
};

const chipTone = (value) => {
  const normalized = value.toLowerCase();
  if (normalized.includes('critical') || normalized === 'risk') return 'critical';
  if (normalized.includes('caution') || normalized === 'warning' || normalized.includes('disclaimer') || normalized.includes('not a diagnosis')) return 'caution';
  if (normalized === 'moderate') return 'moderate';
  if (normalized.includes('low')) return 'low';
  if (normalized === 'strength') return 'strength';
  return 'high';
};

const explicitChipValue = (label, value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return CHIP_LABELS.has(String(label || '').toLowerCase()) && IMPORTANT_VALUES.has(normalized) ? normalized : null;
};

export const classifyPhase5Line = (input) => {
  const text = String(input || '').replace(/\s+/g, ' ').trim();
  const numbered = text.match(/^\d+[.):]\s+/);
  const candidate = numbered ? text.slice(numbered[0].length) : text;
  const innerMatch = candidate.match(labelPattern);
  const match = innerMatch ? Object.assign([...innerMatch], { index: 0 }) : null;
  const standalone = !match && LABELS.find((label) => label.toLowerCase() === text.replace(/:$/, '').toLowerCase());
  const label = (match?.[1] || standalone || '').trim();
  const value = match ? candidate.slice(match[0].length) : '';
  const normalized = label.toLowerCase();
  let kind = 'normal';
  if (CAUTION.has(normalized)) kind = 'caution';
  else if (QUESTION.has(normalized) || (!label && text.endsWith('?'))) kind = 'question';
  else if (ACTION.has(normalized)) kind = 'action';
  else if (IMPORTANT.has(normalized)) kind = 'important';
  else if (EVIDENCE.has(normalized)) kind = 'evidence';
  else if (label) kind = standalone ? 'subheading' : 'label-value';
  return { text, label, value, kind, number: numbered?.[0]?.trim() || '', prefixLength: match ? (numbered?.[0].length || 0) + match[0].length : 0 };
};

const classFor = (kind) => `phase5-structured-block phase5-${kind === 'label-value' ? 'label-value' : `${kind}-block`}`;

const wrapEvidenceMarkers = (root) => {
  const walker = root.ownerDocument.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    if (node.parentElement?.closest('.phase5-evidence')) return;
    const parts = node.nodeValue.split(/(\[Point\s+\d+\])/gi);
    if (parts.length < 2) return;
    const fragment = root.ownerDocument.createDocumentFragment();
    parts.filter(Boolean).forEach((part) => {
      if (/^\[Point\s+\d+\]$/i.test(part)) {
        const marker = root.ownerDocument.createElement('span');
        marker.className = 'phase5-evidence'; marker.textContent = part; fragment.append(marker);
      } else fragment.append(root.ownerDocument.createTextNode(part));
    });
    node.replaceWith(fragment);
  });
};

const splitLabel = (element, prefixLength) => {
  let remaining = prefixLength;
  const walker = element.ownerDocument.createTreeWalker(element, 4 /* NodeFilter.SHOW_TEXT */);
  let endNode; let endOffset = 0;
  while (walker.nextNode()) {
    if (remaining <= walker.currentNode.length) { endNode = walker.currentNode; endOffset = remaining; break; }
    remaining -= walker.currentNode.length;
  }
  if (!endNode) return;
  const range = element.ownerDocument.createRange();
  range.setStart(element, 0); range.setEnd(endNode, endOffset);
  const labelContent = range.extractContents();
  const valueContent = element.ownerDocument.createDocumentFragment();
  while (element.firstChild) valueContent.append(element.firstChild);
  const label = element.ownerDocument.createElement('span'); label.className = 'phase5-label'; label.append(labelContent);
  const value = element.ownerDocument.createElement('span'); value.className = 'phase5-value'; value.append(valueContent);
  element.append(label, value);
};

const structureExplicitLabelList = (element, result) => {
  if (!LIST_VALUE_LABELS.has(result.label.toLowerCase())) return;
  const value = element.querySelector(':scope > .phase5-value');
  if (!value || value.children.length) return;
  const numberedItems = splitExplicitNumberedSequence(value.textContent.replace(/\s*;\s*/g, ' '));
  const items = numberedItems || value.textContent.split(/\s*;\s*/).map((item) => item.trim()).filter(Boolean);
  if (items.length < 2) return;
  const list = element.ownerDocument.createElement(numberedItems ? 'ol' : 'ul');
  list.className = 'phase5-semantic-list';
  items.forEach((item) => {
    const li = element.ownerDocument.createElement('li'); li.textContent = numberedItems ? item.value : item; list.append(li);
  });
  value.replaceChildren(list);
};

const structureExplicitChip = (element, result) => {
  if (!explicitChipValue(result.label, result.value)) return;
  const value = element.querySelector(':scope > .phase5-value');
  if (!value) return;
  const chip = element.ownerDocument.createElement('span');
  chip.className = `phase5-status-chip phase5-chip-${chipTone(result.value)}`;
  chip.textContent = result.value.toUpperCase();
  value.replaceChildren(chip);
};

const appendPlainStructuredValue = (doc, container, label, value, pattern = false) => {
  const block = doc.createElement('div');
  block.className = pattern ? 'phase5-pattern-group' : `phase5-body-text ${classFor(classifyPhase5Line(`${label}: ${value}`).kind)}`;
  if (label) {
    const heading = doc.createElement('div'); heading.className = 'phase5-label'; heading.textContent = pattern ? label.toUpperCase() : label;
    block.append(heading);
  }
  const numbered = splitExplicitNumberedSequence(value.replace(/\s*;\s*/g, ' '));
  const semicolonItems = LIST_VALUE_LABELS.has(label.toLowerCase()) ? value.split(/\s*;\s*/).filter(Boolean) : [];
  if (numbered || semicolonItems.length > 1) {
    const list = doc.createElement(numbered ? 'ol' : 'ul'); list.className = 'phase5-semantic-list';
    (numbered || semicolonItems).forEach((item) => { const li = doc.createElement('li'); li.textContent = numbered ? item.value : item; list.append(li); });
    block.append(list);
  } else {
    const body = doc.createElement('div'); body.className = 'phase5-value';
    if (explicitChipValue(label, value)) {
      const chip = doc.createElement('span'); chip.className = `phase5-status-chip phase5-chip-${chipTone(value)}`; chip.textContent = value.toUpperCase(); body.append(chip);
    } else body.textContent = value;
    block.append(body);
  }
  container.append(block);
};

export const formatPhase5BodyStructure = (html) => {
  if (!html || typeof DOMParser === 'undefined') return html || '';
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  doc.body.querySelectorAll('p, li, h3, h4, h5').forEach((element) => {
    if (element.closest('summary, details > summary')) return;
    const result = classifyPhase5Line(element.textContent);
    const embedded = splitEmbeddedStructuredSections(element.textContent);
    if (embedded) {
      const container = doc.createElement('div'); container.className = 'phase5-embedded-structure';
      embedded.forEach((segment) => appendPlainStructuredValue(doc, container, segment.label, segment.value, segment.pattern));
      element.replaceWith(container);
      return;
    }
    if (result.kind !== 'normal') element.classList.add('phase5-body-text', ...classFor(result.kind).split(' '));
    if (result.number) element.classList.add('phase5-micro-step');
    if (result.label && result.prefixLength) {
      splitLabel(element, result.prefixLength);
      structureExplicitLabelList(element, result);
      structureExplicitChip(element, result);
    }
    else if (result.kind === 'subheading') element.classList.add('phase5-subheading');
    wrapEvidenceMarkers(element);
    if (!result.label && element.tagName === 'P') {
      const numberedItems = splitExplicitNumberedSequence(element.textContent);
      if (numberedItems) {
        const list = doc.createElement('ol'); list.className = 'phase5-semantic-list';
        numberedItems.forEach((item) => { const li = doc.createElement('li'); li.textContent = item.value; list.append(li); });
        element.replaceWith(list);
      }
    }
  });
  return doc.body.innerHTML;
};

export const Phase5StructuredLine = ({ text, render = (value) => value, as: Tag = 'p', className = '' }) => {
  const result = classifyPhase5Line(text);
  const embedded = splitEmbeddedStructuredSections(text);
  if (embedded) return <div className="phase5-embedded-structure">{embedded.map((segment, index) => (
    <div className={segment.pattern ? 'phase5-pattern-group' : `phase5-body-text ${segment.label ? classFor(classifyPhase5Line(`${segment.label}: ${segment.value}`).kind) : ''}`} key={index}>
      {segment.label && <div className="phase5-label">{segment.pattern ? segment.label.toUpperCase() : segment.label}</div>}
      <StructuredValue label={segment.label} value={segment.value} render={render} />
    </div>
  ))}</div>;
  const classes = ['phase5-body-text', result.kind !== 'normal' && classFor(result.kind), className].filter(Boolean).join(' ');
  if (result.label && result.prefixLength) {
    const numberedItems = LIST_VALUE_LABELS.has(result.label.toLowerCase())
      ? splitExplicitNumberedSequence(result.value.replace(/\s*;\s*/g, ' ')) : null;
    const listItems = LIST_VALUE_LABELS.has(result.label.toLowerCase()) && !numberedItems
      ? result.value.split(/\s*;\s*/).map((item) => item.trim()).filter(Boolean) : [];
    return <Tag className={classes}>
      <span className="phase5-label">{result.number && `${result.number} `}{result.label}:</span>{' '}
      <span className="phase5-value">{explicitChipValue(result.label, result.value)
        ? <span className={`phase5-status-chip phase5-chip-${chipTone(result.value)}`}>{result.value.toUpperCase()}</span>
        : numberedItems
        ? <ol className="phase5-semantic-list">{numberedItems.map((item, index) => <li key={index}>{render(item.value)}</li>)}</ol>
        : listItems.length > 1
        ? <ul className="phase5-semantic-list">{listItems.map((item, index) => <li key={index}>{render(item)}</li>)}</ul>
        : render(result.value)}</span>
    </Tag>;
  }
  return <Tag className={classes}>{render(String(text || ''))}</Tag>;
};

const StructuredValue = ({ label, value, render }) => {
  const numbered = splitExplicitNumberedSequence(String(value).replace(/\s*;\s*/g, ' '));
  const semicolonItems = LIST_VALUE_LABELS.has(String(label).toLowerCase()) && !numbered
    ? String(value).split(/\s*;\s*/).map((item) => item.trim()).filter(Boolean) : [];
  const chip = explicitChipValue(label, value);
  if (chip) return <div className="phase5-value"><span className={`phase5-status-chip phase5-chip-${chipTone(value)}`}>{String(value).toUpperCase()}</span></div>;
  if (numbered) return <ol className="phase5-semantic-list">{numbered.map((item, index) => <li key={index}>{render(item.value)}</li>)}</ol>;
  if (semicolonItems.length > 1) return <ul className="phase5-semantic-list">{semicolonItems.map((item, index) => <li key={index}>{render(item)}</li>)}</ul>;
  return <div className="phase5-value">{render(value)}</div>;
};

const explicitListItem = (line) => {
  const ordered = String(line).match(/^\s*\d+[.):]\s+(.+)$/);
  if (ordered) return { ordered: true, value: ordered[1] };
  const unordered = String(line).match(/^\s*[-*•]\s+(.+)$/);
  if (unordered) return { ordered: false, value: unordered[1] };
  return null;
};

// Shared adapter for report fields that arrive as plain text rather than HTML.
// It creates lists only from explicit source markers, or when the caller knows
// the field is itself a list (for example, Reflection Questions).
export const StructuredReportContent = ({ children, list = false }) => {
  const lines = String(children ?? '').replace(/\r\n?/g, '\n').split('\n');
  const nonEmpty = lines.map((line) => line.trim()).filter(Boolean);
  const explicitItems = nonEmpty.map(explicitListItem);
  const allExplicit = explicitItems.length > 0 && explicitItems.every(Boolean);
  const inlineNumbered = nonEmpty.length === 1 ? splitExplicitNumberedSequence(nonEmpty[0]) : null;

  if (inlineNumbered) return <ol className="phase5-semantic-list">
    {inlineNumbered.map((item, index) => <li key={index}>{item.value}</li>)}
  </ol>;

  if (list && nonEmpty.length) {
    return <ul className="phase5-semantic-list">
      {nonEmpty.map((line, index) => <li key={index}>{explicitListItem(line)?.value || line}</li>)}
    </ul>;
  }
  if (allExplicit) {
    const ordered = explicitItems.every((item) => item.ordered);
    const Tag = ordered ? 'ol' : 'ul';
    return <Tag className="phase5-semantic-list">
      {explicitItems.map((item, index) => <li key={index}>{item.value}</li>)}
    </Tag>;
  }
  return <div className="phase5-structured-content">
    {lines.map((line, index) => line.trim()
      ? <Phase5StructuredLine key={index} text={line} />
      : <br key={index} />)}
  </div>;
};
